import { Sidepanel, Text } from 'components';
import { Api, getCurrencySign, titleToLocaleKey } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { COUNTRY_OPTIONS, INITIAL_STATE, SYNC_STYLES } from './constants';
import { normalizeFeature } from './planFeatures';
import { isFreeTier } from './planTier';
import { countryForTeamName } from './teamCountry';
import { ContentHeader, SearchUsers } from 'screens/Admin/components';

const inputClass = 'w-full border border-[#B0B8C4] rounded-md px-3 py-2.5 text-[13px] text-[#20252D] placeholder-[#9CA3AF] bg-[#F9FAFB] focus:bg-white focus:outline-none focus:border-[#7989A0] focus:ring-1 focus:ring-[#B5BDCA] transition-colors';

const featureFieldLabelClass = 'block text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1';

const errorClass = 'text-[#FA3F58] text-[11px] mt-1 font-medium';

const SubscriptionPlanPanel = ({ visible, closeModal, plan, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(INITIAL_STATE);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [tierTemplates, setTierTemplates] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [teamStripeStatus, setTeamStripeStatus] = useState(null);
  const [loadingStripeStatus, setLoadingStripeStatus] = useState(false);
  const dispatch = useDispatch();
  const panelContentRef = useRef(null);

  const isEditing = !!plan;
  const selectedCountry = COUNTRY_OPTIONS.find((c) => c.code === form.countryCode);
  const currency = selectedCountry?.currency || '';
  const selectedTierTemplate = tierTemplates.find((t) => t.tier === form.tier);
  const isBasicTier = isFreeTier(selectedTierTemplate, form.tier);

  // Fetch tier templates on mount
  useEffect(() => {
    async function fetchTierTemplates() {
      setLoadingTiers(true);
      try {
        const res = await Api.get('/admin/subscriptionPlans/tiers');
        setTierTemplates(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Error fetching tier templates:', error);
        setTierTemplates([]);
      }
      setLoadingTiers(false);
    }
    fetchTierTemplates();
  }, []);

  useEffect(() => {
    if (plan) {
      const monthlyPrice = plan.prices?.find((p) => p.interval === 'monthly');
      const yearlyPrice = plan.prices?.find((p) => p.interval === 'yearly');

      setForm({
        countryCode: plan.countryCode || '',
        teamId: plan.teamId?._id || '',
        tier: plan.tier || '',
        monthlyPrice: monthlyPrice?.amount ?? '',
        monthlyDiscount: monthlyPrice?.discount ?? '',
        yearlyPrice: yearlyPrice?.amount ?? '',
        yearlyDiscount: yearlyPrice?.discount ?? '',
        isActive: plan.isActive !== false,
        pointMultiplier: plan.pointMultiplier ?? '',
        features: (() => {
          const multiplier = plan.pointMultiplier;
          return (plan.features || []).map((feature) => {
            const base = { ...feature, isActive: typeof feature.isActive === 'boolean' ? feature.isActive : true };
            if (multiplier && feature.key?.includes('xpoints-earned')) {
              return {
                ...base,
                title: `${multiplier} x Points Earned`,
                description: `Earn points at a ${multiplier}x rate for all your activities`
              };
            }
            return base;
          });
        })(),
        p4pPercent: plan.profitSplit?.p4p ?? 10,
        teamPercent: plan.profitSplit?.team ?? 85,
        athletePercent: 0,
        vatPercent: plan.profitSplit?.vat ?? 5
      });
      setSelectedTeam(plan.teamId);
    } else {
      setForm(INITIAL_STATE);
      setSelectedTeam(null);
      setTeamStripeStatus(null);
    }
    setErrors({});
  }, [plan, visible]);

  useEffect(() => {
    if (form.teamId) {
      fetchTeamStripeStatus(form.teamId);
    } else {
      setTeamStripeStatus(null);
    }
  }, [form.teamId]);


  // Load features from tier template when tier changes
  useEffect(() => {
    if (form.tier && tierTemplates.length > 0 && !plan) {
      const template = tierTemplates.find((t) => t.tier === form.tier);
      if (template) {
        setForm((prev) => {
          const multiplier = prev.pointMultiplier !== '' ? Number(prev.pointMultiplier) : null;
          return {
            ...prev,
            features: template.features.map((f) => {
              if (multiplier && !isNaN(multiplier) && multiplier > 0 && f.key?.includes('xpoints-earned')) {
                return {
                  ...f,
                  isActive: true,
                  title: `${multiplier} x Points Earned`,
                  description: `Earn points at a ${multiplier}x rate for all your activities`
                };
              }
              return { ...f, isActive: true };
            })
          };
        });
      }
    }
  }, [form.tier, tierTemplates, plan]);

  function updateField(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'pointMultiplier' && value !== '') {
        const multiplier = Number(value);
        if (!isNaN(multiplier) && multiplier > 0) {
          next.features = prev.features.map((f) =>
            f.key?.includes('xpoints-earned')
              ? {
                  ...f,
                  title: `${multiplier} x Points Earned`,
                  description: `Earn points at a ${multiplier}x rate for all your activities`
                }
              : f
          );
        }
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  }

  function toggleFeature(index) {
    setForm((prev) => {
      const features = [...prev.features];
      features[index] = { ...features[index], isActive: !features[index].isActive };
      return { ...prev, features };
    });
  }

  
  async function handleSelectTeam(team) {
    if (!team) return;
    setSelectedTeam(team);
    updateField('teamId', team._id);
    updateField('countryCode', '');

    const full = await Api.post('/admin/getUser', { userId: team._id });
    updateField('countryCode', countryForTeamName(full?.country)?.code || '');
  }

  function handleChangeTeam() {
    setSelectedTeam(null);
    updateField('teamId', '');
    updateField('countryCode', '');
  }

  async function fetchTeamStripeStatus(teamId) {
    setLoadingStripeStatus(true);
    setTeamStripeStatus(null);
    try {
      const res = await Api.get(`/admin/teams/stripe/connectStatus?teamId=${teamId}`);
      setTeamStripeStatus(res?.data || null);
    } catch (error) {
      setTeamStripeStatus(null);
    }
    setLoadingStripeStatus(false);
  }

  function scrollToFirstError(errs) {
    const fieldOrder = ['teamId', 'tier', 'monthlyPrice', 'profitSplit'];
    const container = panelContentRef.current;
    if (!container) return;
    for (const field of fieldOrder) {
      if (errs[field]) {
        container.querySelector(`[data-field="${field}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        break;
      }
    }
  }

  function validate() {
    const errs = {};
    if (!form.teamId) errs.teamId = t('subscription.admin.panel.error.teamRequired');
    if (!form.tier) errs.tier = t('subscription.admin.panel.error.tierRequired');
    if (!isBasicTier && form.monthlyPrice === '' && form.yearlyPrice === '') {
      errs.monthlyPrice = t('subscription.admin.panel.error.priceRequired');
    }
    const splitTotal = Number(form.p4pPercent) + Number(form.teamPercent) + Number(form.vatPercent);
    if (splitTotal !== 100) {
      errs.profitSplit = t('subscription.admin.panel.error.profitSplitTotal', { total: splitTotal });
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) scrollToFirstError(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;

    setSaving(true);

    const prices = [];
    if (isBasicTier) {
      prices.push({ amount: 0, interval: 'monthly' });
      prices.push({ amount: 0, interval: 'yearly' });
    } else {
      if (form.monthlyPrice !== '') {
        prices.push({
          amount: Number(form.monthlyPrice),
          interval: 'monthly',
          ...(form.monthlyDiscount !== '' ? { discount: Number(form.monthlyDiscount) } : {})
        });
      }
      if (form.yearlyPrice !== '') {
        prices.push({
          amount: Number(form.yearlyPrice),
          interval: 'yearly',
          ...(form.yearlyDiscount !== '' ? { discount: Number(form.yearlyDiscount) } : {})
        });
      }
    }

    const normalizedFeatures = form.features.map(normalizeFeature);

    const body = {
      teamId: form.teamId,
      tier: form.tier,
      features: normalizedFeatures,
      prices,
      isActive: form.isActive,
      ...(form.pointMultiplier !== '' ? { pointMultiplier: Number(form.pointMultiplier) } : {}),
      profitSplit: {
        p4p: Number(form.p4pPercent),
        team: Number(form.teamPercent),
        athlete: 0,
        vat: Number(form.vatPercent)
      }
    };

    let res;
    if (isEditing) {
      res = await Api.patch(`/admin/subscriptionPlans/update/${plan._id}`, body);
    } else {
      res = await Api.post('/admin/subscriptionPlans/create', body);
    }

    setSaving(false);

    if (res && !res.err) {
      dispatch({
        type: 'HANDLE_RES',
        payload: {
          res: isEditing
            ? t('subscription.admin.panel.success.updated')
            : t('subscription.admin.panel.success.created')
        }
      });
      onSaved?.();
      closeModal();
    }
  }
  
  const syncStatus = plan?.syncStatus?.stripe || 'pending';
  const syncStyle = SYNC_STYLES[syncStatus] || SYNC_STYLES.pending;

  return (
    <Sidepanel wid="640" pad="0" visible={visible} closeModal={closeModal}>
      <ContentHeader
        text={isEditing ? t('subscription.admin.panel.editTitle') : t('subscription.admin.panel.createTitle')}
        closePanel={closeModal}
      />

      <div ref={panelContentRef} className="px-6 py-5">
        {/* Plan Details Section */}
        <div className="space-y-4">
          <p className="text-[14px] font-bold uppercase tracking-widest mb-1">{t('subscription.admin.panel.section.details')}</p>

          {/* Team */}
          <div data-field="teamId">
            <label className={featureFieldLabelClass}>
              {t('subscription.admin.panel.field.team')} <span className="text-[#FA3F58]">*</span>
            </label>
            {!selectedTeam && <SearchUsers userType="team" selected={null} handleSelect={handleSelectTeam} />}
            {errors.teamId && <p className={errorClass}>{errors.teamId}</p>}
            {selectedTeam && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-[#F9FAFB] rounded border border-[#F0F2F4]">
                <img
                  src={selectedTeam.avatar}
                  alt={selectedTeam.name}
                  className="w-6 h-6 rounded-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="text-[11px] text-[#5F6F86] font-medium">{selectedTeam.name}</span>
                <button
                  type="button"
                  onClick={handleChangeTeam}
                  className="ml-auto text-[11px] font-semibold text-[#5F6F86] hover:text-[#FA3F58]"
                >
                  {t('subscription.admin.panel.field.changeTeam')}
                </button>
              </div>
            )}
            {currency && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#5F6F86] bg-[#F0F2F4] px-2 py-0.5 rounded">
                  {currency.toUpperCase()}
                </span>
                <span className="text-[11px] text-[#97A3B5]">{t('subscription.admin.panel.field.autoDetected')}</span>
              </div>
            )}

            {/* Team Stripe Connect Status */}
            {form.teamId && (
              <div className="mt-2">
                {loadingStripeStatus ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#F0F2F4] rounded-md">
                    <span className="w-2 h-2 rounded-full bg-[#B5BDCA] animate-pulse" />
                    <span className="text-[11px] text-[#97A3B5]">{t('subscription.admin.panel.stripeStatus.checking')}</span>
                  </div>
                ) : teamStripeStatus?.connected ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#e8faf2] rounded-md">
                    <span className="w-2 h-2 rounded-full bg-[#27E3A3]" />
                    <span className="text-[11px] font-semibold text-[#059669]">{t('subscription.admin.panel.stripeStatus.connected')}</span>
                    <span className="text-[11px] text-[#97A3B5]">{t('subscription.admin.panel.stripeStatus.connectedHint')}</span>
                  </div>
                ) : (
                  <div className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2.5 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#EAB308] flex-shrink-0" />
                      <span className="text-[11px] font-semibold text-[#B59500]">
                        {t('subscription.admin.panel.stripeStatus.notConnected', { defaultValue: 'No Stripe account connected' })}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#78716C] leading-relaxed pl-3.5">
                      {t('subscription.admin.panel.stripeStatus.noStripeWarning', {
                        defaultValue: 'All subscription revenue will go to P4P. You will need to manually handle payment distribution and reconcile each transfer in the Manual Audit tab.'
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Tier Selector */}
          <div data-field="tier">
            <label className={featureFieldLabelClass}>
              {t('subscription.admin.panel.field.tier')} <span className="text-[#FA3F58]">*</span>
            </label>
            <select
              className={`${inputClass} cursor-pointer appearance-none pr-8`}
              value={form.tier}
              onChange={(e) => updateField('tier', e.target.value)}
              disabled={loadingTiers}
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' fill='none' stroke='%2397A3B5' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
            >
              <option value="">
                {loadingTiers
                  ? t('subscription.admin.panel.field.loadingTiers')
                  : t('subscription.admin.panel.field.selectTier')}
              </option>
              {tierTemplates.map((tier) => (
                <option key={tier.tier} value={tier.tier}>
                  {t(`subscription.plans.${titleToLocaleKey(tier.title)}`, { defaultValue: tier.title })} - {tier.description}
                </option>
              ))}
            </select>
            {errors.tier && <p className={errorClass}>{errors.tier}</p>}
          </div>

          {/* Tier Info Display (read-only) */}
          {selectedTierTemplate && (
            <div className="bg-[#FAFBFC] border border-[#F0F2F4] rounded-md p-3">
              <div className="flex items-center gap-2 mb-2">
                <Text size="14px" weight="600" col="#353E4A">
                  {t(`subscription.plans.${titleToLocaleKey(selectedTierTemplate.title)}`, { defaultValue: selectedTierTemplate.title })}
                </Text>
                {selectedTierTemplate.badge && (
                  <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-[2px] rounded bg-[#FFECEE] text-[#FA3F58]">
                    {selectedTierTemplate.badge === 'Best Value' ? t('subscription.plans.bestValue') : selectedTierTemplate.badge}
                  </span>
                )}
              </div>
              <Text size="12px" col="#97A3B5">
                {selectedTierTemplate.description}
              </Text>
            </div>
          )}
        </div>

        <div className="my-5 border-t border-[#F0F2F4]" />

        {/* Pricing Section */}
        <div className="space-y-4" data-field="monthlyPrice">
          <div className="flex items-center justify-between">
            <p className="text-[14px] font-bold uppercase tracking-widest">{t('subscription.admin.panel.section.pricing')}</p>
            {currency && (
              <span className="text-[11px] font-bold text-[#7989A0] tracking-wider">{currency.toUpperCase()}</span>
            )}
          </div>

          {isBasicTier && (
            <p className="text-[11px] text-[#7989A0] bg-[#F0F2F4] rounded px-2.5 py-1.5">
              {t('subscription.admin.panel.field.basicTierPriceNote')}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={featureFieldLabelClass}>
                {t('subscription.admin.panel.field.monthly')} <span className="text-[#FA3F58]">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`${inputClass} pl-8${isBasicTier ? ' opacity-50 cursor-not-allowed bg-[#F0F2F4]' : ''}`}
                  placeholder="0.00"
                  value={isBasicTier ? 0 : form.monthlyPrice}
                  disabled={isBasicTier}
                  onChange={(e) => updateField('monthlyPrice', e.target.value)}
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#B5BDCA] font-medium">
                  {getCurrencySign(currency)}
                </span>
              </div>
            </div>
            <div>
              <label className={featureFieldLabelClass}>
                {t('subscription.admin.panel.field.yearly')} <span className="text-[#FA3F58]">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`${inputClass} pl-8${isBasicTier ? ' opacity-50 cursor-not-allowed bg-[#F0F2F4]' : ''}`}
                  placeholder="0.00"
                  value={isBasicTier ? 0 : form.yearlyPrice}
                  disabled={isBasicTier}
                  onChange={(e) => updateField('yearlyPrice', e.target.value)}
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#B5BDCA] font-medium">
                  {getCurrencySign(currency)}
                </span>
              </div>
            </div>
          </div>
          {errors.monthlyPrice && <p className={errorClass}>{errors.monthlyPrice}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={featureFieldLabelClass}>
                {t('subscription.admin.panel.field.monthlyDiscount')}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className={`${inputClass} pl-8${isBasicTier ? ' opacity-50 cursor-not-allowed bg-[#F0F2F4]' : ''}`}
                  placeholder="0"
                  value={form.monthlyDiscount}
                  disabled={isBasicTier}
                  onChange={(e) => updateField('monthlyDiscount', e.target.value)}
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#B5BDCA] font-medium">
                  %
                </span>
              </div>
            </div>
            <div>
              <label className={featureFieldLabelClass}>
                {t('subscription.admin.panel.field.yearlyDiscount')}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className={`${inputClass} pl-8${isBasicTier ? ' opacity-50 cursor-not-allowed bg-[#F0F2F4]' : ''}`}
                  placeholder="0"
                  value={form.yearlyDiscount}
                  disabled={isBasicTier}
                  onChange={(e) => updateField('yearlyDiscount', e.target.value)}
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-[#B5BDCA] font-medium">
                  %
                </span>
              </div>
            </div>
          </div>

          {/* Profit Split */}
          <div data-field="profitSplit">
            <div className="flex items-center justify-between mb-2">
              <label className={featureFieldLabelClass}>Profit Split</label>
              {(() => {
                const total = Number(form.p4pPercent) + Number(form.teamPercent) + Number(form.vatPercent);
                return (
                  <span className={`text-[11px] font-bold ${total === 100 ? 'text-[#059669]' : 'text-[#FA3F58]'}`}>
                    Total: {total}%{total === 100 ? ' ✓' : ' ✗'}
                  </span>
                );
              })()}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {/* P4P */}
              <div>
                <label className="block text-[9px] font-bold text-[#7989A0] uppercase tracking-wider mb-1">P4P %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className={inputClass}
                  value={form.p4pPercent}
                  onChange={(e) => updateField('p4pPercent', e.target.value)}
                />
              </div>
              {/* Team */}
              <div>
                <label className="block text-[9px] font-bold text-[#7989A0] uppercase tracking-wider mb-1">Team %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className={inputClass}
                  value={form.teamPercent}
                  onChange={(e) => updateField('teamPercent', e.target.value)}
                />
              </div>
              {/* Athlete (disabled) */}
              <div>
                <label className="block text-[9px] font-bold text-[#B5BDCA] uppercase tracking-wider mb-1">Athlete %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className={`${inputClass} opacity-50 cursor-not-allowed bg-[#F0F2F4]`}
                  value={0}
                  disabled
                  readOnly
                />
              </div>
              {/* VAT */}
              <div>
                <label className="block text-[9px] font-bold text-[#7989A0] uppercase tracking-wider mb-1">VAT %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className={inputClass}
                  value={form.vatPercent}
                  onChange={(e) => updateField('vatPercent', e.target.value)}
                />
              </div>
            </div>
            {errors.profitSplit && <p className={errorClass}>{errors.profitSplit}</p>}
          </div>

          {/* Point Multiplier */}
          <div>
            <label className={featureFieldLabelClass}>Point Multiplier</label>
            <input
              type="number"
              step="0.1"
              min="1"
              placeholder="e.g. 1.25 (leave blank to use global default)"
              value={form.pointMultiplier}
              onChange={(e) => updateField('pointMultiplier', e.target.value)}
              className={inputClass}
            />
            <p className="text-[11px] text-[#97A3B5] mt-1">
              Overrides the global multiplier for this team's subscribers at this tier.
            </p>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between bg-[#FAFBFC] border border-[#F0F2F4] rounded-md px-3.5 py-3">
            <div>
              <p className="text-[13px] font-semibold text-[#353E4A]">{t('subscription.admin.panel.field.planActive')}</p>
              <p className="text-[11px] text-[#97A3B5]">{t('subscription.admin.panel.field.planActiveDesc')}</p>
            </div>
            <button
              type="button"
              onClick={() => updateField('isActive', !form.isActive)}
              className={`relative inline-flex h-[22px] w-[40px] items-center rounded-full transition-colors duration-200 ${form.isActive ? 'bg-[#27E3A3]' : 'bg-[#D2D8DF]'
                }`}
            >
              <span
                className={`inline-block h-[16px] w-[16px] transform rounded-full bg-white shadow-sm transition-transform duration-200 ${form.isActive ? 'translate-x-[20px]' : 'translate-x-[3px]'
                  }`}
              />
            </button>
          </div>
        </div>

        <div className="my-5 border-t border-[#F0F2F4]" />

        {/* Features Section */}
        {form.tier && form.features.length > 0 && (
          <div>
            <p className="text-[11px] font-bold text-[#B5BDCA] uppercase tracking-widest mb-3">
              {t('subscription.admin.panel.section.featuresToggle')}
            </p>
            <p className="text-[11px] text-[#97A3B5] mb-3">{t('subscription.admin.panel.featuresAccessHint')}</p>
            <div className="space-y-2">
              {form.features.map((feature, index) => (
                <div
                  key={feature.key}
                  className={`flex items-start justify-between gap-3 border rounded-md p-3 transition-all ${
                    feature.isActive
                      ? 'bg-[#F9FAFB] border-[#D2D8DF]'
                      : 'bg-[#FAFBFC] border-[#F0F2F4] opacity-60'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Text size="13px" weight="600" col="#353E4A">
                        {t(`subscription.features.${feature.key}`, {
                          defaultValue: feature.title,
                          ...(feature.key?.includes('xpoints-earned')
                            ? { multiplier: form.pointMultiplier !== '' ? Number(form.pointMultiplier) : (selectedTierTemplate?.pointMultiplier ?? 1) }
                            : {})
                        })}
                      </Text>
                      {feature.badge && (
                        <span className="text-[9px] font-bold tracking-wide uppercase px-1.5 py-0.5 rounded bg-[#F0F2F4] text-[#7989A0]">
                          {feature.badge}
                        </span>
                      )}
                    </div>
                    <Text size="11px" col="#97A3B5">
                      {t(`subscription.features.descriptions.${feature.key}`, {
                        defaultValue: feature.description,
                        ...(feature.key?.includes('xpoints-earned')
                          ? { multiplier: form.pointMultiplier !== '' ? Number(form.pointMultiplier) : (selectedTierTemplate?.pointMultiplier ?? 1) }
                          : {})
                      })}
                    </Text>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleFeature(index)}
                    className={`relative inline-flex h-[22px] w-[40px] items-center rounded-full transition-colors duration-200 flex-shrink-0 ${
                      feature.isActive ? 'bg-[#27E3A3]' : 'bg-[#D2D8DF]'
                    }`}
                  >
                    <span
                      className={`inline-block h-[16px] w-[16px] transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        feature.isActive ? 'translate-x-[20px]' : 'translate-x-[3px]'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sync Status (read-only, editing only) */}
        {isEditing && plan.syncStatus && (
          <>
            <div className="my-5 border-t border-[#F0F2F4]" />
            <div>
              <p className="text-[11px] font-bold text-[#B5BDCA] uppercase tracking-widest mb-3">{t('subscription.admin.panel.section.integration')}</p>
              <div className={`flex items-center justify-between ${syncStyle.bg} rounded-md px-3.5 py-3`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${syncStyle.dot}`} />
                  <span className={`text-[13px] font-semibold capitalize ${syncStyle.text}`}>
                    {t('subscription.admin.panel.field.stripeStatus', { status: syncStatus })}
                  </span>
                </div>
                {plan.syncStatus.stripeSyncedAt && (
                  <span className="text-[11px] text-[#97A3B5]">
                    {new Date(plan.syncStatus.stripeSyncedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
              {plan.syncStatus.stripeError && (
                <div className="mt-2 text-[11px] text-[#FA3F58] bg-[#FFECEE] rounded-md px-3 py-2">
                  {plan.syncStatus.stripeError}
                </div>
              )}
            </div>
          </>
        )}

        {/* Stripe Product ID (read-only, editing only) */}
        {isEditing && plan.stripeProductId && (
          <div className="mt-3">
            <p className="text-[10px] text-[#B5BDCA] mb-0.5">{t('subscription.admin.panel.field.stripeProductId')}</p>
            <p className="text-[11px] text-[#97A3B5] font-mono bg-[#FAFBFC] border border-[#F0F2F4] rounded px-2 py-1 inline-block">
              {plan.stripeProductId}
            </p>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6 pb-2">
          <button
            onClick={handleSave}
            disabled={saving || loadingStripeStatus}
            className="w-full flex items-center justify-center gap-2 py-3 text-[14px] font-semibold text-white bg-[#20252D] rounded-md hover:bg-[#353E4A] active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {saving && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {saving ? t('subscription.admin.panel.button.saving') : isEditing ? t('subscription.admin.panel.button.update') : t('subscription.admin.panel.button.create')}
          </button>
        </div>
      </div>
    </Sidepanel>
  );
};

export default SubscriptionPlanPanel;
